import React, { useState, useEffect } from 'react';
import { Steps, Button, Layout, Typography, Input, Select, Progress, Alert } from 'antd';
import { RocketOutlined, BankOutlined, EnvironmentOutlined, CheckCircleOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Step } = Steps;
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

// 🎯 معالج الإعداد الأول - بيظهر مرة واحدة بس لو مفيش أي مؤسسة في القاعدة
// (شوف SetupGate.js). الخطوة الأخيرة إجبارية ومش ممكن تتخطاها: لازم تنجح
// عشان تدخل السيستم، لأنها بتعمّر الدومينز والتقارير والـ Workflows الأساسية
export default function SetupWizard() {
  const [current, setCurrent] = useState(0);
  const [currencies, setCurrencies] = useState([]);

  const [orgData, setOrgData] = useState({ orgid: '', description: '', basecurrency1: 'EGP' });
  const [sites, setSites] = useState([{ site_id: '', description: '' }]);

  const [finishing, setFinishing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get('/api/domains/CURRENCY', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setCurrencies(res.data?.values || []))
      .catch(() => setCurrencies([]));
  }, []);

  const updateSite = (idx, field, value) => {
    setSites(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };
  const addSite = () => setSites(prev => [...prev, { site_id: '', description: '' }]);
  const removeSite = (idx) => setSites(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);

  const canLeaveStep0 = orgData.orgid.trim() && orgData.basecurrency1;
  const canLeaveStep1 = sites.some(s => s.site_id.trim());

  const handleCompleteSetup = async () => {
    setError('');
    setFinishing(true);
    setDone(false);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      setProgress(15);
      setProgressLabel('Creating organization & sites...');
      const orgRes = await axios.post('/api/organizations/save', {
        orgid: orgData.orgid.trim(),
        description: orgData.description,
        basecurrency1: orgData.basecurrency1,
        basecurrency2: orgData.basecurrency1,
        active: true,
        sites: sites.filter(s => s.site_id.trim()),
        currency_rates: []
      }, { headers });

      setProgress(55);
      setProgressLabel('Loading baseline reference data (domains, reports, workflows)...');
      await axios.post('/api/system/seed-baseline', {}, { headers });

      setProgress(100);
      setProgressLabel('Setup complete.');

      // 🎯 نزامن الـ session المحلية بأول مؤسسة/موقع اتربطوا باليوزر
      // الحالي أوتوماتيك في /organizations/save - عشان أي شاشة تفتح بعد
      // كده تلاقي org_id/site_id موجودين على طول من غير لوجين تاني
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const assignedOrg = orgRes.data?.assigned_org_id || orgData.orgid.trim();
      const assignedSite = orgRes.data?.assigned_site_id || sites.find(s => s.site_id.trim())?.site_id;
      localStorage.setItem('user', JSON.stringify({ ...storedUser, org_id: assignedOrg, site_id: assignedSite }));

      setDone(true);
      setTimeout(() => { window.location.href = '/dashboard'; }, 1200);
    } catch (err) {
      setError(err.response?.data?.detail || 'Setup failed. Please review and retry.');
      setFinishing(false);
    }
  };

  const steps = [
    {
      title: 'Organization',
      icon: <BankOutlined />,
      content: (
        <div style={{ maxWidth: 480 }}>
          <Paragraph type="secondary">Every screen in the system is scoped to an organization. Create the first one.</Paragraph>
          <Text strong>Organization ID *</Text>
          <Input
            placeholder="e.g. COC"
            value={orgData.orgid}
            onChange={e => setOrgData({ ...orgData, orgid: e.target.value.toUpperCase() })}
            style={{ marginTop: 6, marginBottom: 18 }}
          />
          <Text strong>Description</Text>
          <Input
            placeholder="Organization name"
            value={orgData.description}
            onChange={e => setOrgData({ ...orgData, description: e.target.value })}
            style={{ marginTop: 6, marginBottom: 18 }}
          />
          <Text strong>Base Currency *</Text>
          <Select
            showSearch
            value={orgData.basecurrency1}
            onChange={v => setOrgData({ ...orgData, basecurrency1: v })}
            style={{ display: 'block', marginTop: 6 }}
            optionFilterProp="children"
          >
            {currencies.length > 0 ? currencies.map(c => (
              <Option key={c.value} value={c.value}>
                {c.value}{c.description ? ` - ${c.description}` : ''}
              </Option>
            )) : <Option value="EGP">EGP</Option>}
          </Select>
        </div>
      )
    },
    {
      title: 'Sites',
      icon: <EnvironmentOutlined />,
      content: (
        <div style={{ maxWidth: 560 }}>
          <Paragraph type="secondary">Add at least one Site under this organization. Users and records get assigned to a site.</Paragraph>
          {sites.map((s, idx) => (
            <div key={idx} style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'center' }}>
              <Input
                placeholder="Site ID"
                value={s.site_id}
                onChange={e => updateSite(idx, 'site_id', e.target.value.toUpperCase())}
                style={{ width: 160 }}
              />
              <Input
                placeholder="Description"
                value={s.description}
                onChange={e => updateSite(idx, 'description', e.target.value)}
              />
              <DeleteOutlined
                style={{ color: sites.length > 1 ? '#d33' : '#ccc', cursor: sites.length > 1 ? 'pointer' : 'default' }}
                onClick={() => removeSite(idx)}
              />
            </div>
          ))}
          <Button icon={<PlusOutlined />} onClick={addSite}>Add Site</Button>
        </div>
      )
    },
    {
      title: 'Finish',
      icon: <CheckCircleOutlined />,
      content: (
        <div style={{ maxWidth: 520 }}>
          <Paragraph>
            <Text strong>Organization:</Text> {orgData.orgid} ({orgData.basecurrency1})<br />
            <Text strong>Sites:</Text> {sites.filter(s => s.site_id.trim()).map(s => s.site_id).join(', ') || '---'}
          </Paragraph>
          <Paragraph type="secondary">
            Completing setup will create the organization and site(s) above, assign your account to them,
            and load baseline reference data (domains, standard reports, standard workflows). This step is
            required and cannot be skipped.
          </Paragraph>

          {(finishing || done) && (
            <div style={{ margin: '20px 0' }}>
              <Progress percent={progress} status={done ? 'success' : 'active'} />
              <Text type="secondary" style={{ fontSize: 12 }}>{progressLabel}</Text>
            </div>
          )}

          {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} showIcon />}

          <Button
            type="primary"
            size="large"
            icon={<RocketOutlined />}
            loading={finishing && !done}
            disabled={done}
            onClick={handleCompleteSetup}
          >
            {done ? 'Done - redirecting...' : 'Complete Setup'}
          </Button>
        </div>
      )
    }
  ];

  return (
    <Layout style={{ padding: '40px', background: '#fff', minHeight: '100vh' }}>
      <Title level={2}><RocketOutlined /> First-Run Setup</Title>
      <Steps current={current} style={{ marginBottom: 40, maxWidth: 700 }}>
        {steps.map(s => <Step key={s.title} title={s.title} icon={s.icon} />)}
      </Steps>

      <div style={{ padding: '32px', background: '#fafafa', border: '1px dashed #d9d9d9', maxWidth: 700 }}>
        {steps[current].content}
      </div>

      <div style={{ marginTop: 24, maxWidth: 700 }}>
        {current > 0 && !finishing && !done && (
          <Button style={{ marginRight: 8 }} onClick={() => setCurrent(current - 1)}>Back</Button>
        )}
        {current === 0 && (
          <Button type="primary" disabled={!canLeaveStep0} onClick={() => setCurrent(1)}>Next Step</Button>
        )}
        {current === 1 && (
          <Button type="primary" disabled={!canLeaveStep1} onClick={() => setCurrent(2)}>Next Step</Button>
        )}
      </div>
    </Layout>
  );
}
